import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, MapPinIcon, BuildingIcon, CalendarIcon, ChevronDownIcon, PencilIcon, SearchIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { trendData, quotesListData, userInsights } from '../../data/biData';
const COLORS = ['#10b981', '#3b82f6', '#ef4444', '#64748b'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const salesManagersByRegion: Record<string, {
  name: string;
  email: string;
  initials: string;
}[]> = {
  'Aisco MENA': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }, {
    name: 'Mohamed S. Aloraini',
    email: 'mohamed.aloraini@aisco.com',
    initials: 'MA'
  }, {
    name: 'Abdullah ALkaff',
    email: 'abdullah.alkaff@aisco.com',
    initials: 'AA'
  }],
  'Aisco AFRICA': [{
    name: 'Khaled Shalabi',
    email: 'khaled.shalabi@aisco.com',
    initials: 'KS'
  }, {
    name: 'Baher Fahem Alani',
    email: 'baher.alani@aisco.com',
    initials: 'BA'
  }, {
    name: 'Essam Fakhry Mohamed',
    email: 'essam.mohamed@aisco.com',
    initials: 'EM'
  }],
  'Aisco EUROPE': [{
    name: 'Régis SIMIER',
    email: 'regis.simier@aisco.com',
    initials: 'RS'
  }, {
    name: 'Javier Adrados',
    email: 'javier.adrados@aisco.com',
    initials: 'JA'
  }, {
    name: 'Daniel Fernández',
    email: 'daniel.fernandez@aisco.com',
    initials: 'DF'
  }, {
    name: 'Olexandr Morozov',
    email: 'olexandr.morozov@aisco.com',
    initials: 'OM'
  }],
  'Aisco AMERICA': [{
    name: 'Diego Solis',
    email: 'diego.solis@aisco.com',
    initials: 'DS'
  }, {
    name: 'Chris Spelic',
    email: 'chris.spelic@aisco.com',
    initials: 'CS'
  }, {
    name: 'Mark Ellis',
    email: 'mark.ellis@aisco.com',
    initials: 'ME'
  }],
  'Aisco ANZ': [{
    name: 'John Quirk',
    email: 'john.quirk@aisco.com',
    initials: 'JQ'
  }]
};
const allRegions = Object.keys(salesManagersByRegion);
export function UserPerformanceView() {
  const [selectedRegion, setSelectedRegion] = useState('Aisco AMERICA');
  const [selectedManagerName, setSelectedManagerName] = useState('Diego Solis');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  const currentManager = Object.values(salesManagersByRegion).flat().find(m => m.name === selectedManagerName) || salesManagersByRegion['Aisco AMERICA'][0];
  const managersForRegion = selectedRegion === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[selectedRegion] || [];
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
    }
    if (isDropdownOpen || isEditingDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isEditingDate]);
  const handleSelectManager = (manager: {
    name: string;
    email: string;
    initials: string;
  }, region: string) => {
    setSelectedManagerName(manager.name);
    if (region !== selectedRegion && selectedRegion !== 'All') {
      setSelectedRegion(region);
    }
    setIsDropdownOpen(false);
  };
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    const managers = region === 'All' ? Object.values(salesManagersByRegion).flat() : salesManagersByRegion[region] || [];
    if (managers.length > 0 && !managers.find(m => m.name === selectedManagerName)) {
      setSelectedManagerName(managers[0].name);
    }
  };
  const statusData = [{
    name: 'Won',
    value: 85
  }, {
    name: 'Active',
    value: 15
  }, {
    name: 'Lost',
    value: 15
  }, {
    name: 'Draft',
    value: 5
  }];
  const userTrendData = trendData.map(d => ({
    ...d,
    total: Math.floor(d.total * 0.15),
    won: Math.floor(d.won * 0.15),
    lost: Math.floor(d.lost * 0.15)
  }));
  return <div className="space-y-6" data-id="element-727">
      {/* Access/Coverage Block */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-728">
        {/* User Identity — now a selector */}
        <div className="flex items-center gap-4 mr-auto relative" ref={dropdownRef} data-id="element-729">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-2 ring-blue-400/30 shadow-lg flex-shrink-0" data-id="element-730">
            <span className="text-lg font-bold text-white" data-id="element-731">
              {currentManager.initials}
            </span>
          </div>
          <div data-id="element-732">
            {/* Clickable name selector */}
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 group text-left" data-id="element-733">
              <h3 className="text-white font-bold text-xl leading-tight group-hover:text-blue-200 transition-colors" data-id="element-734">
                {currentManager.name}
              </h3>
              <ChevronDownIcon size={18} className={`text-blue-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-735" />
            </button>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-736">
              Territory Manager
            </p>
            <p className="text-blue-300/50 text-xs leading-tight mt-0.5" data-id="element-737">
              {currentManager.email}
            </p>

            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-738">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-739">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-740" />
                <span className="font-medium" data-id="element-741">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-742" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-743">
                  <div className="space-y-2.5" data-id="element-744">
                    {/* Period type */}
                    <div data-id="element-745">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-746">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-747">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-748">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-749">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-750">
                          Month
                        </p>
                        <div className="relative" data-id="element-751">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-752">
                            {months.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-753">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-754">
                            <ChevronDownIcon size={12} data-id="element-755" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-756">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-757">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-758">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-759">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-760">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-761">
                        Year
                      </p>
                      <div className="relative" data-id="element-762">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-763">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-764">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-765">
                          <ChevronDownIcon size={12} data-id="element-766" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Dropdown */}
            {isDropdownOpen && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl z-50 min-w-[340px] max-h-[420px] overflow-hidden flex flex-col" data-id="element-767">
                {/* Region filter tabs */}
                <div className="px-3 pt-3 pb-2 border-b border-white/10" data-id="element-768">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-2" data-id="element-769">
                    Region
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-770">
                    <button onClick={() => handleRegionChange('All')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === 'All' ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-771">
                      All
                    </button>
                    {allRegions.map(region => <button key={region} onClick={() => handleRegionChange(region)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${selectedRegion === region ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-772">
                        {region.replace('Aisco ', '')}
                      </button>)}
                  </div>
                </div>

                {/* Search bar */}
                <div className="px-3 pt-2 pb-1 border-b border-white/10" data-id="element-773">
                  <div className="relative" data-id="element-774">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-400/50" data-id="element-775" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or email..." className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-blue-300/40 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" autoFocus data-id="element-776" />
                  </div>
                </div>

                {/* Sales managers list */}
                <div className="overflow-y-auto flex-1 py-1 no-scrollbar" data-id="element-777">
                  {selectedRegion === 'All' ? Object.entries(salesManagersByRegion).map(([region, managers]) => {
                const filtered = managers.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase()));
                if (filtered.length === 0) return null;
                return <div key={region} data-id="element-778">
                              <p className="px-4 pt-2.5 pb-1 text-[10px] uppercase tracking-wider text-blue-400/50 font-medium" data-id="element-779">
                                {region}
                              </p>
                              {filtered.map(manager => <button key={`${region}-${manager.name}`} onClick={() => handleSelectManager(manager, region)} className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-780">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-781">
                                    <span className="text-[10px] font-bold text-white" data-id="element-782">
                                      {manager.initials}
                                    </span>
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-783">
                                    <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-784">
                                      {manager.name}
                                    </p>
                                    <p className="text-xs text-blue-300/50 truncate" data-id="element-785">
                                      {manager.email}
                                    </p>
                                  </div>
                                  {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-786" />}
                                </button>)}
                            </div>;
              }) : managersForRegion.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) || m.email.toLowerCase().includes(searchQuery.toLowerCase())).map(manager => <button key={manager.name} onClick={() => handleSelectManager(manager, selectedRegion)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${selectedManagerName === manager.name ? 'bg-white/5' : ''}`} data-id="element-787">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0" data-id="element-788">
                              <span className="text-[10px] font-bold text-white" data-id="element-789">
                                {manager.initials}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-790">
                              <p className={`text-sm font-medium truncate ${selectedManagerName === manager.name ? 'text-white' : 'text-blue-200'}`} data-id="element-791">
                                {manager.name}
                              </p>
                              <p className="text-xs text-blue-300/50 truncate" data-id="element-792">
                                {manager.email}
                              </p>
                            </div>
                            {selectedManagerName === manager.name && <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" data-id="element-793" />}
                          </button>)}
                </div>
              </div>}
          </div>
        </div>

        {/* Assigned Business Unit Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-794">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-795">
            Assigned Business Units
          </p>
          <div className="flex gap-2" data-id="element-796">
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-797">
              Aisco AMERICA
            </span>
            <span className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-blue-200 font-medium" data-id="element-798">
              Aisco LATAM
            </span>
          </div>
        </div>

        {/* Coverage Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-799">
          <p className="text-blue-300/70 text-xs mb-2 font-medium" data-id="element-800">Coverage</p>
          <div className="flex gap-4" data-id="element-801">
            <div className="flex items-center text-sm text-blue-200" data-id="element-802">
              <MapPinIcon size={14} className="mr-1.5 text-blue-400" data-id="element-803" />
              <span className="font-semibold text-white mr-1" data-id="element-804">4</span> Countries
            </div>
            <div className="flex items-center text-sm text-blue-200" data-id="element-805">
              <BuildingIcon size={14} className="mr-1.5 text-blue-400" data-id="element-806" />
              <span className="font-semibold text-white mr-1" data-id="element-807">12</span> Dealers
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-808">
        <BIKPI title="Total" value={120} trend={12} color="blue" data-id="element-809" />
        <BIKPI title="Won" value={85} trend={8} color="emerald" data-id="element-810" />
        <BIKPI title="Win Rate" value="85.0%" trend={2.5} color="teal" data-id="element-811" />
        <BIKPI title="Active" value={15} color="amber" data-id="element-812" />
        <BIKPI title="Lost" value={15} color="red" data-id="element-813" />
        <BIKPI title="Draft" value={5} color="purple" data-id="element-814" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-815">
        <BICard title="Quotes by Status" className="lg:col-span-1" data-id="element-816">
          <div className="h-64 flex flex-col items-center justify-center" data-id="element-817">
            <ResponsiveContainer width="100%" height="100%" data-id="element-818">
              <PieChart data-id="element-819">
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" data-id="element-820">
                  {statusData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-821" />)}
                </Pie>
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-822" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-823" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Personal Quotes Trend" className="lg:col-span-2" data-id="element-824">
          <div className="h-64" data-id="element-825">
            <ResponsiveContainer width="100%" height="100%" data-id="element-826">
              <LineChart data={userTrendData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-827">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-828" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-829" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-830" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-831" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-832" />
                <Line type="monotone" dataKey="total" name="Total" stroke="#3b82f6" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-833" />
                <Line type="monotone" dataKey="won" name="Won" stroke="#10b981" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-834" />
                <Line type="monotone" dataKey="lost" name="Lost" stroke="#ef4444" strokeWidth={2} dot={{
                r: 3
              }} data-id="element-835" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-836">
        <div className="lg:col-span-2 space-y-6" data-id="element-837">
          <BICard title="Recent Quotes" data-id="element-838">
            <div className="overflow-x-auto" data-id="element-839">
              <table className="w-full text-sm text-left" data-id="element-840">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-841">
                  <tr data-id="element-842">
                    <th className="px-4 py-3 font-medium" data-id="element-843">Quote #</th>
                    <th className="px-4 py-3 font-medium" data-id="element-844">Date</th>
                    <th className="px-4 py-3 font-medium" data-id="element-845">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-846">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-847">Amount</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-848">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-849">
                  {quotesListData.filter(q => q.user === 'Alice Smith').map((quote, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-850">
                        <td className="px-4 py-3 font-medium text-blue-400" data-id="element-851">
                          {quote.id}
                        </td>
                        <td className="px-4 py-3 text-blue-200" data-id="element-852">
                          {quote.date}
                        </td>
                        <td className="px-4 py-3 text-white" data-id="element-853">{quote.dealer}</td>
                        <td className="px-4 py-3 text-blue-300" data-id="element-854">
                          {quote.country}
                        </td>
                        <td className="px-4 py-3 text-right text-white" data-id="element-855">
                          ${quote.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-856">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${quote.status === 'Won' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : quote.status === 'Active' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : quote.status === 'Lost' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'}`} data-id="element-857">
                            {quote.status}
                          </span>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-858">
          <BIInsights insights={userInsights} data-id="element-859" />
        </div>
      </div>
    </div>;
}